import { useState, useEffect } from 'react';
import axios from 'axios';
import { CheckCircle, XCircle, Edit, Calendar, User, Award } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ExamResults = () => {
  const [distributions, setDistributions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showUpdateDialog, setShowUpdateDialog] = useState(false);
  const [selectedDistribution, setSelectedDistribution] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    status: 'Passed',
    score: '',
    test_date: new Date().toISOString().split('T')[0],
    notes: ''
  });

  useEffect(() => {
    fetchDistributions();
  }, []);

  const fetchDistributions = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/distributions`);
      setDistributions(response.data);
    } catch (error) {
      toast.error('Failed to fetch distributions');
    } finally {
      setLoading(false);
    }
  };

  const openUpdateDialog = (distribution) => {
    setSelectedDistribution(distribution);
    setFormData({
      status: distribution.usage_status === 'Passed' ? 'Passed' : distribution.usage_status === 'Failed' ? 'Failed' : 'Passed',
      score: distribution.score || '',
      test_date: distribution.test_date ? new Date(distribution.test_date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
      notes: distribution.admin_notes || ''
    });
    setShowUpdateDialog(true);
  };

  const handleUpdateResult = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('admin_token');
      const payload = {
        status: formData.status,
        score: formData.score ? parseInt(formData.score) : null,
        test_date: formData.test_date,
        notes: formData.notes
      };

      await axios.patch(`${API}/distributions/${selectedDistribution.id}/result`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(`Exam result updated to ${formData.status}`);
      setShowUpdateDialog(false);
      setSelectedDistribution(null);
      fetchDistributions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update result');
    }
  };

  const filteredDistributions = distributions.filter(dist => {
    const matchesSearch = 
      dist.student_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      dist.student_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      dist.voucher_code?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = filterStatus === 'all' || 
      (filterStatus === 'pending' && (!dist.usage_status || dist.usage_status === 'Not Reported')) ||
      (filterStatus === 'passed' && dist.usage_status === 'Passed') ||
      (filterStatus === 'failed' && dist.usage_status === 'Failed');
    
    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (distribution) => {
    if (!distribution.usage_status || distribution.usage_status === 'Not Reported') {
      return <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-300">Pending</Badge>;
    }
    if (distribution.usage_status === 'Passed') {
      return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300">Passed</Badge>;
    }
    if (distribution.usage_status === 'Failed') {
      return <Badge variant="outline" className="bg-red-50 text-red-700 border-red-300">Failed</Badge>;
    }
    return <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-300">Pending</Badge>;
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="exam-results-page">
      <div>
        <h2 className="text-4xl font-bold text-slate-800 dark:text-slate-100 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
          Exam Results Management
        </h2>
        <p className="text-slate-600 dark:text-slate-400">Manually update exam results for students</p>
      </div>

      <Card className="border-slate-200 dark:border-slate-700">
        <CardHeader>
          <CardTitle>Filters</CardTitle>
          <CardDescription>Search and filter distributed vouchers</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4">
            <Input
              placeholder="Search by student name, email, or voucher code..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-1"
            />
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="passed">Passed</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200 dark:border-slate-700">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Award className="w-5 h-5" />
            Distributed Vouchers ({filteredDistributions.length})
          </CardTitle>
          <CardDescription>View and manage exam results</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            </div>
          ) : (
            <div className="rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50 dark:bg-slate-800">
                    <TableHead>Student</TableHead>
                    <TableHead>Voucher Code</TableHead>
                    <TableHead>Certification</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Score</TableHead>
                    <TableHead>Test Date</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredDistributions.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center text-slate-500 h-32">
                        No distributions found
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredDistributions.map((dist) => (
                      <TableRow key={dist.id}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4 text-slate-400" />
                            <div>
                              <div className="font-medium">{dist.student_name}</div>
                              <div className="text-xs text-slate-500">{dist.student_email}</div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="font-mono text-sm">{dist.voucher_code}</TableCell>
                        <TableCell>{dist.certification_type || 'N/A'}</TableCell>
                        <TableCell>{getStatusBadge(dist)}</TableCell>
                        <TableCell>
                          {dist.score ? (
                            <span className="font-medium">{dist.score}%</span>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {dist.test_date ? (
                            <div className="flex items-center gap-1 text-sm">
                              <Calendar className="w-3 h-3 text-slate-400" />
                              {new Date(dist.test_date).toLocaleDateString()}
                            </div>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openUpdateDialog(dist)}
                            className="flex items-center gap-1"
                          >
                            <Edit className="w-3 h-3" />
                            Update
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Update Result Dialog */}
      <Dialog open={showUpdateDialog} onOpenChange={setShowUpdateDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Update Exam Result</DialogTitle>
            <DialogDescription>
              Manually enter exam results for {selectedDistribution?.student_name}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdateResult} className="space-y-4">
            <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg">
              <div className="text-sm text-blue-800 dark:text-blue-200">
                <div><strong>Student:</strong> {selectedDistribution?.student_name}</div>
                <div><strong>Voucher:</strong> {selectedDistribution?.voucher_code}</div>
                <div><strong>Certification:</strong> {selectedDistribution?.certification_type}</div>
              </div>
            </div>

            <div>
              <Label htmlFor="status">Exam Status *</Label>
              <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
                <SelectTrigger className="mt-2">
                  <SelectValue placeholder="Select result" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Passed">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      Passed
                    </div>
                  </SelectItem>
                  <SelectItem value="Failed">
                    <div className="flex items-center gap-2">
                      <XCircle className="w-4 h-4 text-red-600" />
                      Failed
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="score">Score (Optional)</Label>
              <Input
                id="score"
                type="number"
                min="0"
                max="100"
                value={formData.score}
                onChange={(e) => setFormData({ ...formData, score: e.target.value })}
                placeholder="e.g., 85"
                className="mt-2"
              />
              <p className="text-xs text-slate-500 mt-1">Enter score as a percentage (0-100)</p>
            </div>

            <div>
              <Label htmlFor="test_date">Test Date</Label>
              <Input
                id="test_date"
                type="date"
                value={formData.test_date}
                onChange={(e) => setFormData({ ...formData, test_date: e.target.value })}
                className="mt-2"
              />
            </div>

            <div>
              <Label htmlFor="notes">Admin Notes (Optional)</Label>
              <textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="w-full mt-2 min-h-[80px] px-3 py-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950"
                placeholder="Add any additional notes..."
              />
            </div>

            <div className="flex gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowUpdateDialog(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-gradient-to-r from-emerald-500 to-teal-400 text-white hover:from-emerald-600 hover:to-teal-500"
              >
                Update Result
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ExamResults;
